import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import PhoneInput from "@/components/PhoneInput";
import { toast } from "sonner";

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";

  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [legacyClient, setLegacyClient] = useState("no");  // "yes" | "no"
  const [legacyDate, setLegacyDate] = useState("");        // YYYY-MM-DD
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !middleName.trim() || !lastName.trim()) {
      toast.error("يرجى إدخال الاسم الأول واسم الأب واسم العائلة");
      return;
    }
    if (!phone.trim()) {
      toast.error("رقم جوال Botim مطلوب");
      return;
    }
    if (password.length < 6) {
      toast.error("كلمة المرور يجب أن لا تقل عن 6 أحرف");
      return;
    }
    if (legacyClient === "yes" && !legacyDate) {
      toast.error("يرجى تحديد تاريخ أول جلسة سابقة مع الأستاذة");
      return;
    }
    setLoading(true);
    try {
      await register({
        first_name: firstName,
        middle_name: middleName,
        last_name: lastName,
        email,
        phone,
        password,
        legacy_client: legacyClient === "yes",
        legacy_first_session_date: legacyClient === "yes" ? legacyDate : null,
      });
      toast.success("تم إنشاء الحساب بنجاح");
      navigate(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || "تعذّر إنشاء الحساب");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell>
      <section className="max-w-md mx-auto px-6 py-16">
        <div className="card-warm p-8">
          <div className="flex justify-center mb-4">
            <img
              src="https://customer-assets.emergentagent.com/job_counsel-marketplace/artifacts/60nvz0lu_%D8%A3%D9%86%D8%A7%20%D8%A7%D9%84%D8%AE%D8%A7%D9%84%D8%AF%D9%8A%20%28%D8%A7%D9%84%D9%84%D9%88%D9%82%D9%88%20%D8%A7%D9%84%D9%86%D9%87%D8%A7%D8%A6%D9%8A%29.png"
              alt="أنا الخالدي"
              className="h-28 sm:h-32 w-auto object-contain select-none"
              draggable="false"
            />
          </div>
          <h1 className="font-heading text-3xl text-[#222222]">إنشاء حساب</h1>
          <p className="text-[#5E5C58] text-sm mt-2">
            املأ بياناتك الكاملة حتى نتمكن من تجهيز جلساتك والتواصل معك عبر Botim.
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label htmlFor="fn">الاسم الأول</Label>
                <Input
                  id="fn"
                  data-testid="register-first-name-input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="mn">اسم الأب</Label>
                <Input
                  id="mn"
                  data-testid="register-middle-name-input"
                  value={middleName}
                  onChange={(e) => setMiddleName(e.target.value)}
                  required
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="ln">اسم العائلة</Label>
                <Input
                  id="ln"
                  data-testid="register-last-name-input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className="mt-2"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="email">البريد الإلكتروني</Label>
              <Input
                id="email"
                data-testid="register-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2"
                dir="ltr"
              />
            </div>
            <div>
              <Label htmlFor="phone">رقم جوال Botim</Label>
              <PhoneInput
                id="phone"
                testId="register-phone"
                value={phone}
                onChange={setPhone}
                required
                className="mt-2"
                placeholder="5XXXXXXXX"
              />
              <p className="text-xs text-[#5E5C58] mt-1">
                يُستخدم هذا الرقم للتواصل خلال جلساتك عبر تطبيق Botim.
              </p>
            </div>
            <div>
              <Label htmlFor="password">كلمة المرور</Label>
              <PasswordInput
                id="password"
                data-testid="register-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="mt-2"
              />
            </div>

            {/* Iteration 46 — legacy-client question */}
            <div className="rounded-xl border border-[#E5E1D8] bg-[#F6F4EE]/40 p-4 space-y-3">
              <Label className="text-[#222222] font-medium">
                هل سبق لكِ الحجز مع الأستاذة؟
              </Label>
              <div className="flex items-center gap-6">
                <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="radio"
                    name="legacyClient"
                    value="no"
                    checked={legacyClient === "no"}
                    onChange={() => { setLegacyClient("no"); setLegacyDate(""); }}
                    data-testid="register-legacy-no"
                    className="accent-[#0A4D3C]"
                  />
                  لا، أنا عميل جديد
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="radio"
                    name="legacyClient"
                    value="yes"
                    checked={legacyClient === "yes"}
                    onChange={() => setLegacyClient("yes")}
                    data-testid="register-legacy-yes"
                    className="accent-[#0A4D3C]"
                  />
                  نعم، سبق لي
                </label>
              </div>
              {legacyClient === "yes" && (
                <div data-testid="register-legacy-date-wrap">
                  <Label htmlFor="legacy-date" className="text-xs text-[#5E5C58]">
                    تاريخ أول جلسة سابقة
                  </Label>
                  <Input
                    id="legacy-date"
                    data-testid="register-legacy-date-input"
                    type="date"
                    value={legacyDate}
                    onChange={(e) => setLegacyDate(e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                    className="mt-1"
                    dir="ltr"
                  />
                </div>
              )}
            </div>

            <Button
              data-testid="register-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white h-11"
            >
              {loading ? "جاري الإنشاء..." : "إنشاء الحساب"}
            </Button>
          </form>
          <p className="text-sm text-[#5E5C58] mt-6 text-center">
            لديك حساب بالفعل؟{" "}
            <Link to="/login" data-testid="register-go-login-link" className="text-[#0A4D3C] underline">
              تسجيل الدخول
            </Link>
          </p>
        </div>
      </section>
    </PageShell>
  );
};

export default Register;
